import { Children, createElement, isValidElement } from 'react';
import styled from '@emotion/styled';

import BaseLink from '@/components/common/BaseLink/BaseLink';
import { screen } from '@/components/common/breakpoints';
import { HeadingLevelProvider } from '@/components/common/Heading/context';
import { Heading, List, Paragraph } from '@/components/common/MarkUp';
import {
  determineDestination,
  PortableText,
  VisuallyHidden,
} from '@/components/common/utils';
import {
  headingMedium,
  headingSmall,
  subheadingSmall,
} from '@/styles/typography';

import type { SanityBlogPostTocDataType } from '@/components/page/blog/PostTableOfContents';
import type {
  PortableTextBlock,
  PortableTextReactComponents,
} from '@portabletext/react';

// Prevents links rendering inside links
const filterLinks = (children: React.ReactNode) => {
  const linkMarks = ['internalLink', 'link'];

  return Children.map(children, (child) => {
    if (isValidElement(child)) {
      if (linkMarks.includes(child.props.markType)) {
        return child.props.text;
      }
    }

    return child;
  });
};

const getComponents = (): Partial<PortableTextReactComponents> => {
  return {
    block: ({ value, children }) => {
      switch (value.style) {
        case 'normal':
          return <Paragraph size="body-lg">{children}</Paragraph>;
        case 'h2':
          return (
            <HeadingLevelProvider level={2}>
              <Heading size="heading-lg">{children}</Heading>
            </HeadingLevelProvider>
          );
        case 'h3':
          return (
            <HeadingLevelProvider level={3}>
              <Heading size="heading-md">{children}</Heading>
            </HeadingLevelProvider>
          );
        case 'h4':
          return (
            <HeadingLevelProvider level={4}>
              <Heading size="heading-sm">{children}</Heading>
            </HeadingLevelProvider>
          );
        default:
          return children;
      }
    },
    marks: {
      strong: ({ children }) => <b>{children}</b>,
      internalLink: ({ value, children }) => {
        let prefix = '';

        switch (value?.item?._type) {
          case 'blogPost':
            prefix = '/blog';
            break;
          case 'programme': {
            const { audienceRef, region } = value.item;

            const basePath = audienceRef ? audienceRef.basePath : '';
            const programmesPath =
              region === 'en-GB' ? 'programmes' : 'programs';

            prefix = `${basePath}/${programmesPath}`;
            break;
          }
          case 'page':
          default: {
            const audienceRef = value?.item?.audienceRef;
            prefix = audienceRef?.basePath ?? '';
            break;
          }
        }

        const href = `${prefix}/${value.item?.slug.current}`;

        return <BaseLink href={href}>{children}</BaseLink>;
      },
      link: ({ value, children }) => {
        const { href } = value;

        if (!href) {
          return <span>{children}</span>;
        }

        const destination = determineDestination(href);
        const sanitizedChildren = filterLinks(children);
        return (
          <BaseLink
            href={href}
            target={destination.target}
            rel={
              destination.isExternalLink ? 'external noopener noreferrer' : null
            }
          >
            {sanitizedChildren}
            {destination.isExternalLink && (
              <VisuallyHidden>{destination.newTabText}</VisuallyHidden>
            )}
          </BaseLink>
        );
      },
    },
    list: ({ value, children }) => {
      return (
        <List size="body-lg" type={value.listItem === 'bullet' ? 'ul' : 'ol'}>
          {children}
        </List>
      );
    },
    listItem: ({ children }) => (
      <ListItem key={Math.random()}>{children}</ListItem>
    ),
  };
};

export interface PostBodyProps {
  block: PortableTextBlock;
  id: string;
  style: string;
  marks: Array<string>;
  list?: string;
  UlList?: Array<Array<PortableTextBlock>>;
  OlList?: Array<Array<PortableTextBlock>>;
  tocData: SanityBlogPostTocDataType;
}

const PostBody = ({
  block,
  style,
  marks,
  list,
  id,
  UlList,
  OlList,
  tocData,
}: PostBodyProps) => {
  const strong = marks && marks[0] === 'strong';

  const displayBlock = getDisplayBlock(block, OlList, UlList);

  const isList = displayBlock?.length > 0;

  let element = {
    component: null,
    props: {},
  };

  const key = block._key;
  let index: number;

  switch (style) {
    case 'h2':
      index = tocData.headingKeys.indexOf(key);
      element.component = HeadingContainer;
      element.props = {
        name: tocData.headingStrings[index],
        id: tocData.headingIds[index],
      };
      break;
    case 'h3':
      element.component = Heading3Container;
      break;
    case 'h4':
      element.component = Heading4Container;
      break;
    case 'blockquote':
      element = {
        component: QuoteContainer,
        props: { weight: (strong && 500) || 400 },
      };
      break;
    case 'quoteatt':
      element.component = AttrContainer;
      break;
    default:
      if (!list) {
        element.component = DefaultBlock;
      }
  }

  if (isList && (list === 'bullet' || list === 'number')) {
    element = {
      component: ListContainer,
      props: { id },
    };
  }

  if (!element.component) {
    return null;
  }

  createElement(
    element.component,
    element.props,
    <PortableText value={displayBlock} components={getComponents()} />,
  );

  return (
    <Container>
      <ContentContainer>
        {createElement(
          element.component,
          element.props,
          <PortableText value={displayBlock} components={getComponents()} />,
        )}
      </ContentContainer>
    </Container>
  );
};

const getDisplayBlock = (block, OlList, UlList) => {
  let displayBlock = [];
  let ulBlock = [];
  let olBlock = [];

  if (UlList.length > 0) {
    if (Array.isArray(UlList[0])) {
      UlList?.forEach((ulArr) => {
        if (block._key === ulArr[0]._key) {
          ulBlock = ulArr;
        }
      });
    } else if (block._key === UlList[0]._key) {
      ulBlock = UlList;
    }
  }
  if (OlList.length > 0) {
    if (Array.isArray(OlList[0])) {
      OlList?.forEach((olArr) => {
        if (block._key === olArr[0]._key) {
          olBlock = olArr;
        }
      });
    } else if (block._key === OlList[0]._key) {
      olBlock = OlList;
    }
  }

  displayBlock =
    (olBlock?.length > 0 && olBlock) ||
    (ulBlock?.length > 0 && ulBlock) ||
    block;

  return displayBlock;
};

const DefaultBlock = styled.div`
  padding: var(--spacing-100) 0;

  ${screen.md} {
    padding: 0 var(--spacing-400) 0 0;
    width: 100%;
  }
`;

const ListItem = styled.li`
  margin-left: 1em;

  li {
    margin-bottom: 0;
  }
`;
const Container = styled.div`
  ${screen.md} {
    padding: var(--spacing-small) 0 var(--spacing-xx-small);
  }
`;
const ContentContainer = styled.div``;
const QuoteContainer = styled(Paragraph)<{ weight: number }>`
  display: flex;
  padding: var(--spacing-medium) var(--spacing-300) var(--spacing-medium) 0;
  width: 90%;
  margin: 0 auto;
  font-weight: ${(props) => props.weight};
  ${headingMedium}

  em,
  b,
  a {
    display: contents;
    color: var(--text-action);
  }
  ${screen.md} {
    font-weight: ${(props) => props.weight};
    width: 94%;
  }
`;
const AttrContainer = styled.div`
  display: flex;
  padding: var(--spacing-500) var(--spacing-300);
  width: 100%;
  max-width: 628px;
  margin: 0 auto;
  white-space: pre;
  a {
    color: var(--text-action);
  }
  b {
    font-weight: 500;
  }
  ${screen.md} {
    margin: 0;
    max-width: 100%;
    padding: var(--spacing-500) var(--spacing-300) var(--spacing-500)
      calc(var(--spacing-2000) * 2);
  }
`;
const HeadingContainer = styled.div`
  ${headingSmall}
  font-weight: var(--font-weight-semibold);

  a {
    color: var(--text-action);
  }
  em {
    font-style: italic;
  }

  padding: var(--spacing-x-small) 0;

  ${screen.md} {
    margin-bottom: 0px;
    padding: 0 var(--spacing-700) 0 0;
    width: 100%;
  }
`;
const Heading3Container = styled(HeadingContainer)``;
const Heading4Container = styled(HeadingContainer)`
  ${subheadingSmall}
`;
const ListContainer = styled.div`
  ul,
  ol {
    margin-top: 20px;
    padding-left: var(--spacing-100);
    a {
      color: var(--text-action);
    }
  }
  ${screen.md} {
    width: 100%;
  }
`;
export { PostBody };
